import { useEffect, useRef, useState } from "react";

import { SEARCH_DEBOUNCE_MS } from "@/core/constants";
import { getErrorMessage } from "@/core/services";
import { listFornecedoresDisponiveis } from "./fornecedor-conflito.gateway";
import type { FornecedorDisponivel } from "./recursos.types";

export const FORNECEDORES_ERROR_MESSAGE = "Não foi possível carregar os fornecedores.";

export interface UseFornecedoresDisponiveisParams {
  /** Período da alocação da obra escolhida (`yyyy-MM-dd`). */
  dataInicio: string;
  dataFim: string;
  ignorarObraId: string;
  busca: string;
}

export interface FornecedoresDisponiveisState {
  status: "loading" | "success" | "error";
  fornecedores: FornecedorDisponivel[];
  errorMessage: string | null;
}

/** Busca os fornecedores candidatos a substituto; digitar na busca dispara a consulta após o debounce. */
export function useFornecedoresDisponiveis({
  dataInicio,
  dataFim,
  ignorarObraId,
  busca,
}: UseFornecedoresDisponiveisParams): FornecedoresDisponiveisState {
  const [state, setState] = useState<FornecedoresDisponiveisState>({
    status: "loading",
    fornecedores: [],
    errorMessage: null,
  });
  const isFirst = useRef(true);
  const term = busca.trim();

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setState((previous) => ({ ...previous, status: "loading", errorMessage: null }));

    const timer = setTimeout(
      () => {
        isFirst.current = false;
        listFornecedoresDisponiveis({
          dataInicio,
          dataFim,
          ignorarObraId,
          busca: term,
          signal: controller.signal,
        })
          .then((fornecedores) => {
            if (!cancelled) setState({ status: "success", fornecedores, errorMessage: null });
          })
          .catch((error: unknown) => {
            if (cancelled) return;
            setState({
              status: "error",
              fornecedores: [],
              errorMessage: getErrorMessage(error) || FORNECEDORES_ERROR_MESSAGE,
            });
          });
      },
      isFirst.current ? 0 : SEARCH_DEBOUNCE_MS,
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [dataInicio, dataFim, ignorarObraId, term]);

  return state;
}
